import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentHomework = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSubject, setFilterSubject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchHomeworks();
  }, []);

  const fetchHomeworks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/homework', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHomeworks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Extraire les matières uniques
  const subjects = [...new Set(homeworks.map((hw) => hw.subject))];

  // Date du jour (pour comparer)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Parser une date dd/mm/yyyy en objet Date
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  // Statut d'un devoir
  const getStatus = (hw) => {
    const dueDate = parseDate(hw.due_date);
    if (!dueDate) return 'unknown';
    if (dueDate >= today) return 'upcoming';
    return 'past';
  };

  // Filtrer
  const filteredHomeworks = homeworks.filter((hw) => {
    if (filterSubject && hw.subject !== filterSubject) return false;
    if (filterStatus && getStatus(hw) !== filterStatus) return false;
    return true;
  });

  // Stats
  const totalCount = homeworks.length;
  const upcomingCount = homeworks.filter((hw) => getStatus(hw) === 'upcoming').length;
  const pastCount = homeworks.filter((hw) => getStatus(hw) === 'past').length;

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📚 Mes devoirs</h1>
        <p className="text-gray-500">Consultez les devoirs et téléchargez les documents</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-blue-500">
          <p className="text-sm text-gray-500">Total devoirs</p>
          <p className="text-2xl font-bold text-blue-600">{totalCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-orange-500">
          <p className="text-sm text-gray-500">À venir</p>
          <p className="text-2xl font-bold text-orange-600">{upcomingCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-gray-500">
          <p className="text-sm text-gray-500">Passés</p>
          <p className="text-2xl font-bold text-gray-600">{pastCount}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Toutes les matières</option>
            {subjects.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="upcoming">⏳ À venir</option>
            <option value="past">✅ Passés</option>
          </select>

          <button
            onClick={() => {
              setFilterSubject('');
              setFilterStatus('');
            }}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
          >
            🔄 Réinitialiser
          </button>
        </div>
      </div>

      {/* Liste des devoirs */}
      {filteredHomeworks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">📭 Aucun devoir trouvé</p>
          <p className="text-sm">Aucun devoir ne correspond à vos critères.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredHomeworks.map((hw) => {
            const status = getStatus(hw);
            const isPast = status === 'past';

            return (
              <div
                key={hw.id}
                className={`bg-white rounded-xl shadow-md p-5 border-l-4 ${
                  isPast ? 'border-gray-400 opacity-75' : 'border-[#E67E22]'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-bold text-[#2C3E50]">{hw.title}</h3>
                    <p className="text-sm text-gray-500">
                      {hw.subject} • {hw.class_name}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isPast ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {isPast ? '✅ Passé' : '⏳ À venir'}
                  </span>
                </div>

                {hw.description && (
                  <p className="text-sm text-gray-600 mb-3">{hw.description}</p>
                )}

                <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                  <span>📅 À rendre le <strong>{hw.due_date}</strong></span>
                  <span>Publié le {hw.created_at}</span>
                </div>

                {/* Document téléchargeable */}
                {hw.document_url ? (
                  <a
                    href={hw.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center space-x-2 bg-[#E67E22] text-white px-3 py-2 rounded-lg text-sm hover:bg-[#D35400] transition"
                  >
                    <span>📎</span>
                    <span>Télécharger le document</span>
                  </a>
                ) : (
                  <p className="text-xs text-gray-400 italic">Aucun document joint</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentHomework;